/** Alinhado a `Baruk.Know.InDoc.Domain.Enums` — evita importar `documentSignatureService` (axios) em testes Jest. */
import {
  isCancelledSignatureRequestStatus,
  isCompletedSignatureRequestStatus,
  isInProgressSignatureRequestStatus,
  isPendingSignatureRequestStatus,
  isSignerSigned,
  SignatureRequestStatusCode,
  SignatureRuleCode,
  SignatureStatusCode,
} from './signatureStatusUtils';

export type SignatureObligationSigner = {
  isRequired: boolean;
  status: number;
};

export type SignatureObligationRequest = {
  status: number;
  signatureRule: number;
  minSignatures: number;
  completedAt?: string | null;
  documentVersionId?: string | null;
  signers: SignatureObligationSigner[];
};

export type SignatureObligationState = 'none' | 'pending' | 'inProgress' | 'satisfied';

function normalizeGuidString(value: unknown): string | null {
  if (value === undefined || value === null) {
    return null;
  }
  const raw = String(value).trim();
  if (!raw) {
    return null;
  }
  const t = raw.toLowerCase().replace(/-/g, '').replace(/[{}]/g, '');
  if (!t) {
    return null;
  }
  return t;
}

function isCompletedSignatureRequestStatusLocal(status: unknown): boolean {
  return isCompletedSignatureRequestStatus(status);
}

function isSignerSignedLocal(status: unknown): boolean {
  return isSignerSigned(status);
}

/** Espelha `DocumentSignature.TryMarkCompletedIfQuorumSatisfied` (domínio). */
export function isSignatureQuorumSatisfied(signature: SignatureObligationRequest | null | undefined): boolean {
  if (!signature?.signers?.length) {
    return false;
  }

  const signedCount = signature.signers.filter((s) => isSignerSignedLocal(s.status)).length;

  if (signature.signatureRule === SignatureRuleCode.Any) {
    const min = signature.minSignatures > 0 ? signature.minSignatures : 1;
    return signedCount >= min;
  }

  const requiredSigners = signature.signers.filter((s) => s.isRequired);
  if (requiredSigners.length > 0) {
    return requiredSigners.every((s) => isSignerSignedLocal(s.status));
  }

  return signature.signers.every((s) => isSignerSignedLocal(s.status));
}

export function signatureMatchesPublishedVersion(
  signature: SignatureObligationRequest,
  publishedVersionId: string | null | undefined,
  currentVersionId: string | null | undefined
): boolean {
  const sig = normalizeGuidString(signature.documentVersionId);
  if (!sig) {
    return true;
  }
  const pub = normalizeGuidString(publishedVersionId);
  const cur = normalizeGuidString(currentVersionId);
  if (pub && sig === pub) {
    return true;
  }
  if (cur && sig === cur) {
    return true;
  }
  if (!pub && !cur) {
    return true;
  }
  if (pub && cur && pub === cur && sig !== pub) {
    return false;
  }
  return false;
}

function pickSignatureForObligation(
  active: SignatureObligationRequest | null | undefined,
  latest: SignatureObligationRequest | null | undefined
): SignatureObligationRequest | null {
  if (active && !isCancelledSignatureRequestStatus(active.status)) {
    return active;
  }
  if (latest && !isCancelledSignatureRequestStatus(latest.status)) {
    return latest;
  }
  return active ?? latest ?? null;
}

export function resolveSignatureObligation(params: {
  requiresSignature: boolean;
  applyStandaloneSignatureRules: boolean;
  activeSignature?: SignatureObligationRequest | null;
  latestSignature?: SignatureObligationRequest | null;
  publishedVersionId?: string | null;
  currentVersionId?: string | null;
}): SignatureObligationState {
  if (!params.requiresSignature || !params.applyStandaloneSignatureRules) {
    return 'none';
  }

  const signature = pickSignatureForObligation(params.activeSignature, params.latestSignature);

  if (!signature) {
    return 'pending';
  }

  if (isPendingSignatureRequestStatus(signature.status)) {
    return 'pending';
  }

  if (isCancelledSignatureRequestStatus(signature.status)) {
    return 'pending';
  }

  const versionOk = signatureMatchesPublishedVersion(
    signature,
    params.publishedVersionId,
    params.currentVersionId
  );

  if (!versionOk) {
    return 'inProgress';
  }

  const quorumOk = isSignatureQuorumSatisfied(signature);
  const completed =
    isCompletedSignatureRequestStatusLocal(signature.status) ||
    (typeof signature.completedAt === 'string' && signature.completedAt.trim().length > 0);

  if (quorumOk && (completed || isInProgressSignatureRequestStatus(signature.status))) {
    return 'satisfied';
  }

  return 'inProgress';
}

export function blocksPostPublishActionsUntilSignature(obligation: SignatureObligationState): boolean {
  return obligation === 'pending' || obligation === 'inProgress';
}
